/**
 * Client for the dcx-detect microservice. Grabs the active viewport's
 * canvas as a PNG blob, POSTs it to /detect/<tool>, and converts the
 * returned image-pixel coordinates back to world (mm) coordinates via
 * the viewport's canvasToWorld projection.
 *
 * All fetches go to the SAME origin under /detect/*. In production the
 * viewer's IIS web.config proxies /detect/* → the dcx-detect uvicorn
 * process on port 8001 on the same server, mirroring how /atlrestapi/*
 * is proxied to EDGEPACS.
 */
import type { ChiroPoint } from './types';

/** Tools that currently have a real detector on the server side. */
export const SUPPORTED_DETECTIONS = new Set<number>([300]);

/**
 * Direct-port URL to the dcx-detect microservice on port 8001. Uvicorn
 * is configured with the site's TLS cert (viewer.mistiqrad.com,
 * demo.mistiqrad.com — see dcx_detect_service.md), so HTTPS pages call
 * `https://<hostname>:8001/detect/*` cleanly, no mixed content and no
 * IIS URL Rewrite proxy needed.
 *
 * Protocol is inherited from window.location so HTTP dev servers still
 * work (port 8001 speaks whichever protocol uvicorn was launched with).
 * Override with `VITE_DETECT_URL` (build-time env) to point elsewhere.
 */
const DETECT_BASE: string =
  (import.meta.env.VITE_DETECT_URL as string | undefined)?.replace(/\/$/, '') ||
  (typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.hostname}:8001`
    : 'http://localhost:8001');

/** Route the tool id to its endpoint. Falls back to the generic stub
 *  for anything without a bespoke detector. */
function endpointFor(toolId: number): string {
  if (toolId === 300) return `${DETECT_BASE}/detect/cobb`;
  return `${DETECT_BASE}/detect/generic/${toolId}`;
}

interface RawResponse {
  tool_id: number;
  /** Detection stage that produced the points — e.g. 'hough-strict',
   *  'hough-medium', 'hough-loose', or 'fallback-grid' when the pipeline
   *  couldn't find real landmarks. The wizard shows a warning banner
   *  for 'fallback-grid' so the user knows to place points manually. */
  method: string;
  points: { x: number; y: number }[];
}

export interface DetectResult {
  points: ChiroPoint[];
  method: string;
  fellBack: boolean;
}

/**
 * Runs auto-detection against the given viewport for the given tool.
 * Returns the detected points already converted into the viewport's
 * world coordinate system, plus the detector's own report of which
 * stage produced them (`method`).
 *
 * Throws on network / decode / HTTP errors — callers should show a
 * user-visible toast and fall back to manual clicking.
 */
/** Longest edge sent to dcx-detect. VFN's internal input is 1024x512 —
 *  anything larger just burns bandwidth without helping detection. */
const UPLOAD_MAX_EDGE = 1024;
/** JPEG quality for the upload. VFN downscales aggressively so
 *  0.9 is well past the point of introducing detection artifacts. */
const UPLOAD_JPEG_QUALITY = 0.9;

export async function autoDetectPoints(
  viewport: any,
  toolId: number,
): Promise<DetectResult> {
  const canvas = viewport?.getCanvas?.() as HTMLCanvasElement | undefined;
  if (!canvas) throw new Error('viewport has no canvas');

  // Downscale to at most UPLOAD_MAX_EDGE on the longest side, encode
  // as JPEG. On a HiDPI display the raw canvas PNG can be 5-15 MB;
  // this cuts the upload to ~100-300 KB, which is the difference
  // between "instant" and "10 seconds" on a typical uplink.
  const srcW = canvas.width;
  const srcH = canvas.height;
  const longest = Math.max(srcW, srcH);
  const scale = longest > UPLOAD_MAX_EDGE ? UPLOAD_MAX_EDGE / longest : 1;
  const uploadW = Math.max(1, Math.round(srcW * scale));
  const uploadH = Math.max(1, Math.round(srcH * scale));

  const off = document.createElement('canvas');
  off.width = uploadW;
  off.height = uploadH;
  const ctx = off.getContext('2d');
  if (!ctx) throw new Error('no 2d context on offscreen canvas');
  ctx.drawImage(canvas, 0, 0, uploadW, uploadH);

  const blob: Blob = await new Promise((resolve, reject) => {
    off.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
      'image/jpeg',
      UPLOAD_JPEG_QUALITY,
    );
  });

  const form = new FormData();
  form.append('image', blob, 'viewport.jpg');

  const res = await fetch(endpointFor(toolId), { method: 'POST', body: form });
  if (!res.ok) {
    throw new Error(`detect endpoint returned HTTP ${res.status}`);
  }
  const json = (await res.json()) as RawResponse;
  if (!Array.isArray(json.points) || json.points.length === 0) {
    throw new Error('detect endpoint returned no points');
  }

  // Convert returned pixel coords → world via canvasToWorld.
  //
  // dcx-detect returns points in the pixel space of the image we
  // uploaded, which is (uploadW x uploadH) — not the original canvas
  // buffer. canvasToWorld expects CSS pixels (rect.width/height). So
  // the scale factor is CSS_size / upload_size, applied per axis.
  //
  // This subsumes the earlier DPR-only rescale: whether the source
  // canvas is 1x or 2x DPR, the ratio rect.width/uploadW gives the
  // correct mapping.
  const rect = canvas.getBoundingClientRect();
  const sx = rect.width / uploadW;
  const sy = rect.height / uploadH;
  const points = json.points.map((p) => {
    const world = viewport.canvasToWorld([p.x * sx, p.y * sy]);
    return { worldX: world[0], worldY: world[1], worldZ: world[2] ?? 0 };
  });
  return {
    points,
    method: json.method,
    fellBack: json.method === 'fallback-grid',
  };
}
